/**
 * Display formatting for the contract's `Hyperparams` — the five LoRA knobs the
 * product resolves for the guided path and a professional may override.
 *
 * "One path, two depths", rule 2: what the product chose for the user is
 * VISIBLE and CHANGEABLE. The same rows therefore render in two places — the
 * train dialog's "Chosen for you" block (from `TrainingEstimate.hyperparams`,
 * the values the run will actually use) and the model detail's resolved
 * configuration card (from the recorded `Model.hyperparams`) — so the pre-run
 * promise and the post-run record read identically.
 *
 * Pure functions, no auto-imports: the portal's vitest runs in a plain node
 * environment.
 */
import type { components } from "@scalty/contracts";

type Hyperparams = components["schemas"]["Hyperparams"];

/** The five knobs the contract carries, in the order both surfaces list them. */
export const HYPERPARAM_KNOBS = ["rank", "alpha", "learningRate", "epochs", "maxSeqLen"] as const;

export type HyperparamKnob = (typeof HYPERPARAM_KNOBS)[number];

/** A knob the user has explicitly set; every omitted knob takes the resolved guided value. */
export type HyperparamOverrides = Partial<Record<HyperparamKnob, number>>;

/** The em dash stands in for "the server has not told us yet" — never a guessed number. */
const UNKNOWN = "—";

export function formatLearningRate(value: number | undefined | null): string {
  return typeof value === "number" ? value.toExponential() : UNKNOWN;
}

export function formatRankAlpha(
  rank: number | undefined | null,
  alpha: number | undefined | null,
): string {
  return typeof rank === "number" && typeof alpha === "number" ? `${rank} / ${alpha}` : UNKNOWN;
}

export function formatEpochs(value: number | undefined | null): string {
  return typeof value === "number" ? String(value) : UNKNOWN;
}

export function formatMaxSeqLen(value: number | undefined | null): string {
  return typeof value === "number" ? value.toLocaleString() : UNKNOWN;
}

export interface HyperparamRow {
  label: string;
  value: string;
}

/**
 * The resolved configuration as label/value rows. A null/absent set yields the
 * same four rows with em dashes rather than a fabricated preset.
 */
export function hyperparamRows(hp: Hyperparams | null | undefined): HyperparamRow[] {
  return [
    { label: "Rank / alpha", value: formatRankAlpha(hp?.rank, hp?.alpha) },
    { label: "Learning rate", value: formatLearningRate(hp?.learningRate) },
    { label: "Epochs", value: formatEpochs(hp?.epochs) },
    { label: "Sequence length", value: formatMaxSeqLen(hp?.maxSeqLen) },
  ];
}

/**
 * A one-line summary of the resolved configuration for the base-model card.
 * Returns null when nothing has been resolved yet — the caller renders an
 * honest "resolved from your data" note instead of a made-up preset.
 */
export function hyperparamSummary(hp: Hyperparams | null | undefined): string | null {
  if (
    hp == null ||
    hp.rank === undefined ||
    hp.alpha === undefined ||
    hp.learningRate === undefined ||
    hp.epochs === undefined ||
    hp.maxSeqLen === undefined
  ) {
    return null;
  }
  return `Chosen for you: rank ${hp.rank}, alpha ${hp.alpha}, lr ${formatLearningRate(
    hp.learningRate,
  )}, ${hp.epochs} epochs, ${formatMaxSeqLen(hp.maxSeqLen)} window`;
}

/**
 * The values the pro-override inputs display: the user's own override where one
 * exists, otherwise the server's resolved value, otherwise null (nothing
 * resolved yet — the input renders empty rather than a guessed default).
 */
export function effectiveHyperparams(
  resolved: Hyperparams | null | undefined,
  overrides: HyperparamOverrides,
): Record<HyperparamKnob, number | null> {
  const out = {} as Record<HyperparamKnob, number | null>;
  for (const knob of HYPERPARAM_KNOBS) {
    out[knob] = overrides[knob] ?? resolved?.[knob] ?? null;
  }
  return out;
}

/**
 * The `hyperparams` field of the TrainRequest: ONLY the knobs the professional
 * changed. Omitting a knob is what lets the server resolve its adaptive guided
 * value — sending a client-side default would silently defeat it.
 */
export function overridesToRequest(overrides: HyperparamOverrides): Hyperparams | undefined {
  const out: HyperparamOverrides = {};
  let any = false;
  for (const knob of HYPERPARAM_KNOBS) {
    const value = overrides[knob];
    if (value !== undefined) {
      out[knob] = value;
      any = true;
    }
  }
  return any ? out : undefined;
}
